import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, CheckCircle2, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { BRAND } from "@/data/site";
import { ICON_MAP } from "@/data/highlightIcons";

const HERO = "https://images.unsplash.com/photo-1689574666551-52eb11c37bcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const FALLBACK_IMG = "https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const anchor = (t, i) => `svc-${i}-${(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

function ServicesHero({ c, services }) {
  return (
    <section className="relative overflow-hidden bg-navy-void" data-testid="services-hero">
      <img src={c.hero_image || HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" data-testid="services-hero-image" />
      <div className="absolute inset-0 bg-gradient-to-r from-navy-void via-navy/85 to-navy/40" />
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-14 sm:pt-24 sm:pb-20">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-xs font-semibold uppercase tracking-[0.2em] text-teal mb-3">What We Do</motion.p>
        <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] max-w-3xl">
          {c.hero_title || "Our Services"}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="mt-5 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">{c.hero_subtitle}</motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-8 flex flex-wrap gap-2">
          {services.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Building2;
            return (
              <a key={i} href={`#${anchor(s.title, i)}`} data-testid={`services-jump-${i}`}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs sm:text-sm font-medium text-white backdrop-blur-md hover:bg-teal hover:border-teal transition-colors">
                <Icon className="h-4 w-4" /> {s.title}
              </a>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

function ServiceRow({ s, i }) {
  const Icon = ICON_MAP[s.icon] || Building2;
  const flip = i % 2 === 1;
  const points = (s.points || []).filter(Boolean);
  return (
    <section id={anchor(s.title, i)} className={`scroll-mt-24 py-12 sm:py-16 ${flip ? "bg-slate-50" : "bg-white"}`} data-testid={`service-section-${i}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid gap-8 lg:gap-14 lg:grid-cols-2 items-center">
        <Reveal className={`relative ${flip ? "lg:order-2" : ""}`}>
          <div className="overflow-hidden rounded-3xl shadow-xl">
            <img src={s.image || FALLBACK_IMG} alt={s.title} className="h-64 sm:h-96 w-full object-cover transition-transform duration-700 hover:scale-105" data-testid={`service-image-${i}`} />
          </div>
          <div className={`absolute -bottom-5 ${flip ? "right-5" : "left-5"} flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-slate-100`}>
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Icon className="h-6 w-6" /></span>
            <span className="font-display text-2xl font-bold text-teal">{String(i + 1).padStart(2, "0")}</span>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="pt-4 lg:pt-0">
          {s.tagline && <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal mb-2">{s.tagline}</p>}
          <h2 className="font-display text-2xl sm:text-4xl font-bold text-slate-900 leading-tight">{s.title}</h2>
          {s.desc && <p className="mt-4 text-base sm:text-lg font-medium text-slate-700 leading-relaxed">{s.desc}</p>}
          {s.body && <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed whitespace-pre-line">{s.body}</p>}
          {points.length > 0 && (
            <ul className="mt-6 grid gap-3 sm:grid-cols-2" data-testid={`service-points-${i}`}>
              {points.map((p, k) => (
                <li key={k} className="flex items-start gap-2.5 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal" /> {p}
                </li>
              ))}
            </ul>
          )}
          <Link to="/contact" data-testid={`service-enquire-${i}`}
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-dark transition-colors">
            Enquire about this service <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Process({ c }) {
  const steps = c.process || [];
  if (!steps.length) return null;
  return (
    <section className="py-14 sm:py-20 bg-navy grain-overlay relative overflow-hidden" data-testid="services-process">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal><h2 className="font-display text-2xl sm:text-4xl font-bold text-white">{c.process_title || "How We Work"}</h2></Reveal>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((st, i) => {
            const Icon = ICON_MAP[st.icon] || Building2;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 hover:bg-white/10 transition-colors" data-testid={`process-step-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal text-white"><Icon className="h-6 w-6" /></span>
                    <span className="font-display text-3xl font-bold text-white/15">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <p className="mt-4 font-semibold text-white">{st.title}</p>
                  <p className="mt-1 text-xs sm:text-sm text-white/65">{st.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesCta({ c }) {
  return (
    <section className="py-12 sm:py-16" data-testid="services-cta">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 rounded-3xl bg-slate-50 ring-1 ring-slate-100 px-6 py-9 sm:px-12">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{c.cta_title || "Not sure which service fits you?"}</h2>
            <p className="mt-2 text-sm sm:text-base text-slate-500">{c.cta_body}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="services-cta-contact" className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-3 text-sm font-semibold text-white hover:bg-teal-dark transition-colors">Talk to Our Team <ArrowRight className="h-4 w-4" /></Link>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer" data-testid="services-cta-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-navy px-6 py-3 text-sm font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><MessageCircle className="h-4 w-4" /> WhatsApp Us</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  useSeo("services");
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/content/services").then((r) => setC(r.data || {})).catch(() => setC({}));
  }, []);
  if (!c) return <div className="min-h-[60vh]" />;
  const services = c.services || [];
  return (
    <div data-testid="services-page">
      <ServicesHero c={c} services={services} />
      {services.map((s, i) => <ServiceRow key={i} s={s} i={i} />)}
      <Process c={c} />
      <ServicesCta c={c} />
    </div>
  );
}
